import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { fetchPublicTrainers } from '../widget-api/public-trainers';
import { useAsync } from '../widget-hooks/useAsync';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { hasWidgetAuthQuery, widgetAuthQueryFromSearchParams } from '../widget-lib/widget-auth-query';
import { PATH, pathTrainer } from '../widget-lib/path-routes';
import { MediaAvatar } from '../widget-ui/media-avatar';
import { PromoBanner } from '../widget-ui/promo-banner';

export default function TrainersListPage() {
  useWidgetSeo(
    'Тренеры и инструкторы',
    'Наша команда квалифицированных тренеров и инструкторов. Информация, специализация и онлайн-запись.'
  );
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const suffix = useQuerySuffix();
  const { orgSlug, bootstrapHint, accessToken, widgetConfig, guestShowcaseOrg } = useEmbedBootstrap();

  const rawBranchId = searchParams.get('branch_id');
  const branchId = rawBranchId ? Number(rawBranchId) : null;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(branchId);

  useEffect(() => {
    setSelectedBranchId(branchId);
  }, [branchId]);

  const branches = widgetConfig?.branches ?? guestShowcaseOrg?.branches ?? [];

  const hasAuthOrKey = Boolean(
    accessToken || hasWidgetAuthQuery(searchParams) || searchParams.get('embed_global') === '1'
  );
  const canFetch = Boolean(orgSlug && hasAuthOrKey);
  const qsKey = searchParams.toString();

  const { data: trainers, loading, error } = useAsync(
    () => {
      if (!orgSlug || !canFetch) return Promise.resolve([]);
      return fetchPublicTrainers(
        orgSlug,
        widgetAuthQueryFromSearchParams(searchParams),
        accessToken,
        selectedBranchId
      );
    },
    [orgSlug, canFetch, qsKey, accessToken, selectedBranchId]
  );

  const handleBranchChange = (branchIdVal: string) => {
    const nextId = branchIdVal ? Number(branchIdVal) : null;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextId != null) {
        next.set('branch_id', String(nextId));
      } else {
        next.delete('branch_id');
      }
      return next;
    });
  };

  const filteredTrainers = useMemo(() => {
    if (!trainers) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return trainers;
    return trainers.filter((t) =>
      (t.full_name && t.full_name.toLowerCase().includes(q)) ||
      (t.specialization && t.specialization.toLowerCase().includes(q)) ||
      (t.description && t.description.toLowerCase().includes(q))
    );
  }, [trainers, searchQuery]);

  if (bootstrapHint) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Тренеры</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Тренеры</h1>
        <p className="text-sm text-[var(--text-muted)]">Укажите в ссылке параметр org_slug (slug организации).</p>
      </div>
    );
  }

  if (!hasAuthOrKey) {
    return (
      <Navigate
        to={`${PATH.signIn}${suffix}`}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return (
    <div className="space-y-4 w-full min-w-0">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Тренеры</h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative flex-1">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-[var(--text-muted)] pointer-events-none">
              🔍
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по имени или специализации..."
              className="w-full rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] py-1.5 pl-8 pr-4 text-xs text-[var(--text)] outline-none transition-all focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
            />
          </div>
          {branches.length > 1 && (
            <select
              value={selectedBranchId ?? ''}
              onChange={(e) => handleBranchChange(e.target.value)}
              className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-1.5 text-xs text-[var(--text)] outline-none transition-all focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
            >
              <option value="">Все филиалы</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
      <PromoBanner screen="trainers" />
      {loading ? (
        <ul className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <li
              key={i}
              className="flex items-center gap-4 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)]"
            >
              <div className="h-14 w-14 shrink-0 animate-pulse rounded-full bg-[var(--bg-soft)]" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 animate-pulse rounded bg-[var(--bg-soft)]" />
                <div className="h-3 w-24 animate-pulse rounded bg-[var(--bg-soft)]" />
              </div>
            </li>
          ))}
        </ul>
      ) : error ? (
        <p className="text-sm text-[var(--error-text)]">{error}</p>
      ) : !filteredTrainers?.length ? (
        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/50 px-4 py-10 text-center shadow-[var(--shadow-card)]">
          <p className="text-sm text-[var(--text-muted)]">Список тренеров пуст.</p>
        </div>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {filteredTrainers.map((t) => {
            const name = t.full_name || `Тренер #${t.id}`;
            return (
              <li key={t.id}>
                <Link
                  to={`${pathTrainer(t.id)}${suffix}`}
                  className="flex min-w-0 items-center gap-4 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-4 shadow-[var(--shadow-card)] transition-shadow duration-200 hover:shadow-md active:scale-[0.99]"
                >
                  <MediaAvatar
                    photoUrl={t.photo_url}
                    label={name}
                    sizeClass="h-14 w-14 shrink-0"
                    roundedClass="rounded-full ring-2 ring-[var(--border)]"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[var(--text)]">{name}</p>
                    {t.specialization ? (
                      <p className="mt-0.5 truncate text-xs text-[var(--accent)]">{t.specialization}</p>
                    ) : null}
                    {t.description ? (
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[var(--text-muted)]">
                        {t.description}
                      </p>
                    ) : null}
                  </div>
                  <span className="shrink-0 text-[var(--text-muted)]" aria-hidden>
                    ›
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
